import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Crown,
  LogOut,
  MoreVertical,
  User,
  HelpCircle,
  Mail,
  Code2,
  AppWindowMac,
  Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SidebarSeparator } from "@/components/ui/sidebar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Import components
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageToggle } from "@/components/language-toggle";

// Import services
import { useTheme } from "@/hooks/useTheme";
import { LibraryBig, Lock } from "lucide-react";
import { useFeatures } from "@/hooks/useFeatures";
import { useAppDispatch } from "@/hooks/redux";
import { logoutUser } from "@/store/slices/authSlice";
// Types
type UserData = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  isEmailVerified: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  createdAt: string;
  updatedAt: string;
  userType: "individual" | "company";
  companyName: string | null;
  siret: string | null;
};

interface DashboardSidebarProps {
  sidebarOpen: boolean;
  user: UserData | null;
  selectedView: string;
  setSelectedView: (view: string) => void;
  isMobile?: boolean;
}

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  sidebarOpen,
  user,
  selectedView,
  setSelectedView,
  isMobile = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { hasFeature } = useFeatures();
  // State for contact support dialog
  const [isContactSupportOpen, setIsContactSupportOpen] = React.useState(false);

  // Use the passed mobile prop
  const isActuallyMobile = isMobile;

  // Menu items configuration - show all features with access indicators
  const menuItems = [
    {
      id: "code-generator",
      title: t("sidebar.menuItems.codeGenerator"),
      icon: Code2,
      onClick: () => setSelectedView("code-generator"),
      requiredFeature: "code_generation",
      hasAccess: hasFeature("code_generation"),
    },
    {
      id: "variable_library",
      title: t("sidebar.menuItems.library"),
      icon: LibraryBig,
      onClick: () => setSelectedView("variable_library"),
      requiredFeature: "variable_library",
      hasAccess: hasFeature("variable_library"),
    },
    {
      id: "code-editor",
      title: t("sidebar.menuItems.codeEditor"),
      icon: AppWindowMac,
      onClick: () => setSelectedView("code-editor"),
      requiredFeature: "code_editor",
      hasAccess: hasFeature("code_editor"),
    },
    {
      id: "ai-generator",
      title: t("sidebar.menuItems.aiGenerator"),
      icon: Brain,
      onClick: () => setSelectedView("ai-generator"),
      requiredFeature: "ai_silae",
      hasAccess: hasFeature("ai_silae"),
    },
  ];

  // Handle menu item click - always allow navigation
  const handleMenuItemClick = (item: (typeof menuItems)[0]) => {
    // Always navigate to the feature, regardless of access
    item.onClick();
  };

  // Helper function to open email client
  const handleContactSupport = () => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      `${t("sidebar.support.title")} - ${import.meta.env.VITE_APP_NAME}`,
    );
    const currentDate = new Date().toLocaleDateString("fr-FR");
    const currentTime = new Date().toLocaleTimeString("fr-FR");

    const body = encodeURIComponent(
      `${t("sidebar.support.emailTemplate.greeting")}

${t("sidebar.support.emailTemplate.needAssistance")}
${t("sidebar.support.emailTemplate.describeProblem")}


${t("sidebar.support.emailTemplate.problemType")}
${t("sidebar.support.emailTemplate.connectionIssue")}
${t("sidebar.support.emailTemplate.chatbotIssue")}
${t("sidebar.support.emailTemplate.billingIssue")}
${t("sidebar.support.emailTemplate.technicalIssue")}
${t("sidebar.support.emailTemplate.usageQuestion")}
${t("sidebar.support.emailTemplate.featureRequest")}
${t("sidebar.support.emailTemplate.other")}


${t("sidebar.support.emailTemplate.technicalDetails")}
- ${t("sidebar.support.emailTemplate.browserUsed")} ${
        navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
            ? "Firefox"
            : navigator.userAgent.includes("Safari")
              ? "Safari"
              : navigator.userAgent.includes("Edge")
                ? "Edge"
                : t("sidebar.support.emailTemplate.other")
      }
- ${t("sidebar.support.emailTemplate.operatingSystem")} ${
        navigator.platform || t("sidebar.support.emailTemplate.notDefined")
      }
- ${t("sidebar.support.emailTemplate.dateTime")} ${currentDate} à ${currentTime}
- ${t("sidebar.support.emailTemplate.pageUrl")} ${window.location.href}


${t("sidebar.support.emailTemplate.userInfo")}
- ${t("sidebar.support.emailTemplate.email")} ${
        user?.email || t("sidebar.support.emailTemplate.notProvided")
      }
- ${t("sidebar.support.emailTemplate.fullName")} ${user?.firstName || ""} ${user?.lastName || ""}
- ${t("sidebar.support.emailTemplate.subscriptionPlan")} ${
        user?.subscriptionPlan || t("sidebar.freeUser")
      }
- ${t("sidebar.support.emailTemplate.subscriptionStatus")} ${
        user?.subscriptionStatus ||
        t("sidebar.support.emailTemplate.notDefined")
      }
- ${t("sidebar.support.emailTemplate.emailVerified")} ${
        user?.isEmailVerified
          ? t("sidebar.support.emailTemplate.yes")
          : t("sidebar.support.emailTemplate.no")
      }


${t("sidebar.support.emailTemplate.stepsToReproduce")}
1. 
2. 
3. 

${t("sidebar.support.emailTemplate.expectedResult")}


${t("sidebar.support.emailTemplate.actualResult")}


${t("sidebar.support.emailTemplate.thankYou")}

${t("sidebar.support.emailTemplate.regards")}
${user?.firstName || ""} ${user?.lastName || ""}`,
    );

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  // Helper functions
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  if (isActuallyMobile) {
    // Mobile version - custom implementation
    return (
      <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
        {/* Sidebar Header */}
        <div className="border-b border-sidebar-border p-2 pb-0">
          <div className="flex h-[56px] items-center px-2">
            <div className="flex items-center gap-2">
              <img
                src={
                  sidebarOpen || isActuallyMobile
                    ? isDark
                      ? "/assets/logo_full_dark.webp"
                      : "/assets/logo_full_light.webp"
                    : isDark
                      ? "/assets/logo_dark.webp"
                      : "/assets/logo_light.webp"
                }
                alt="logo"
                className="h-8  transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 overflow-auto p-2">
          <div className="space-y-1">
            {menuItems.map((item) => (
              <div key={item.id}>
                <Button
                  variant={selectedView === item.id ? "secondary" : "ghost"}
                  className={`w-full justify-start h-12 ${!item.hasAccess ? "opacity-60" : ""}`}
                  onClick={() => handleMenuItemClick(item)}
                >
                  <item.icon className="h-5 w-5 mr-2" />
                  <span className="flex-1 text-left">{item.title}</span>
                  {!item.hasAccess && (
                    <Lock className="h-4 w-4 ml-2 text-neutral-300" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="border-t border-sidebar-border p-2 ">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start h-auto p-2 touch-manipulation"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    {user?.userType === "individual"
                      ? getInitials(user?.firstName || "", user?.lastName || "")
                      : getInitials(user?.companyName || "", "")}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 text-left ml-2 min-w-0 group-data-[collapsible=icon]:hidden">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">
                      {user?.userType === "individual"
                        ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                          t("dashboard.defaultUserName")
                        : user?.companyName || ""}
                    </span>
                    {user?.subscriptionPlan && (
                      <Crown className="h-3 w-3 text-yellow-500 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.subscriptionPlan || t("sidebar.freeUser")}
                  </p>
                </div>

                <MoreVertical className="h-4 w-4 ml-auto text-muted-foreground group-data-[collapsible=icon]:hidden" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={`w-64 p-1 ${isActuallyMobile ? "w-72" : ""}`}
              align="end"
              side={isActuallyMobile ? "top" : "right"}
            >
              <div className="space-y-1">
                {/* Navigation Options */}
                <Button
                  variant={selectedView === "profile" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => setSelectedView("profile")}
                >
                  <User className="h-4 w-4 mr-2" />
                  {t("dashboard.tabs.profile")}
                </Button>
                <Button
                  variant={
                    selectedView === "subscription" ? "secondary" : "ghost"
                  }
                  className="w-full justify-start"
                  onClick={() => setSelectedView("subscription")}
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {t("dashboard.tabs.subscription")}
                </Button>

                {/* Contact Support Dialog */}
                <Dialog
                  open={isContactSupportOpen}
                  onOpenChange={setIsContactSupportOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="ghost" className="w-full justify-start">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      {t("sidebar.support.title")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5" />
                        {t("sidebar.support.title")}
                      </DialogTitle>
                      <DialogDescription>
                        {t("sidebar.support.description")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                          <div className="flex items-center gap-3">
                            <Mail className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">
                                {t("sidebar.support.emailSupport")}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {import.meta.env.VITE_SUPPORT_EMAIL}
                              </p>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleContactSupport}
                          className="w-full"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {t("sidebar.support.openEmailApp")}
                        </Button>

                        <p className="text-xs text-muted-foreground text-center">
                          {t("sidebar.support.emailAppNote")}
                        </p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <SidebarSeparator className="my-1" />

                {/* Theme & Language Settings */}
                <div className="flex items-center justify-between px-2 py-1">
                  <span className="text-sm text-muted-foreground">
                    {t("dashboard.appearance")}
                  </span>
                  <div className="flex items-center gap-1">
                    <ThemeToggle />
                    <LanguageToggle />
                  </div>
                </div>

                <SidebarSeparator className="my-1" />
                <Button
                  variant="ghost"
                  className="w-full justify-start text-red-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                  onClick={async () => {
                    try {
                      await dispatch(logoutUser()).unwrap();
                      navigate("/login");
                    } catch (error) {
                      console.error("Logout error:", error);
                      // Even if logout fails, navigate to login
                      navigate("/login");
                    }
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("auth.logout")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    );
  }

  // Desktop version - custom inline sidebar
  return (
    <div className="flex flex-col h-full ">
      {/* Sidebar Header */}
      <div className="border-b border-sidebar-border p-2 h-16">
        <div className="flex h-[56px] items-center px-2">
          <div className="flex items-center gap-2">
            <img
              src={
                sidebarOpen || isActuallyMobile
                  ? isDark
                    ? "/assets/logo_full_dark.webp"
                    : "/assets/logo_full_light.webp"
                  : isDark
                    ? "/assets/logo_dark.webp"
                    : "/assets/logo_light.webp"
              }
              alt="logo"
              className="h-8 transition-all duration-300 w-auto"
            />
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="flex-1 overflow-auto p-2 pt-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <div key={item.id}>
              <div
                className={`w-full h-12 flex bg-muted items-center rounded-md px-3 cursor-pointer ${
                  sidebarOpen ? "justify-start" : "justify-center px-2"
                } ${
                  selectedView === item.id
                    ? "bg-primary text-primary-foreground"
                    : ""
                } ${!item.hasAccess ? "opacity-60" : ""}`}
                onClick={() => handleMenuItemClick(item)}
                title={!sidebarOpen ? item.title : undefined}
              >
                <item.icon className="h-6 w-6" />
                {sidebarOpen && (
                  <span className="ml-2 font-medium text-sm flex-1">
                    {item.title}
                  </span>
                )}
                {!item.hasAccess && sidebarOpen && (
                  <Lock className="h-4 w-4 text-neutral-300 " />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sidebar Footer */}
      <div className="border-t border-sidebar-border p-2 h-16">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full h-auto p-2 touch-manipulation ${
                sidebarOpen ? "justify-start" : "justify-center"
              }`}
            >
              <Avatar className="h-8 w-8 overflow-visible">
                <AvatarFallback>
                  {user?.userType === "individual"
                    ? getInitials(user?.firstName || "", user?.lastName || "")
                    : getInitials(user?.companyName || "", "")}
                </AvatarFallback>
              </Avatar>

              {sidebarOpen && (
                <>
                  <div className="flex-1 text-left ml-2 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {user?.userType === "individual"
                          ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                            t("dashboard.defaultUserName")
                          : user?.companyName || ""}
                      </span>
                      {user?.subscriptionPlan && (
                        <Crown className="h-3 w-3 text-yellow-500 shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.userType == "company"
                        ? t("sidebar.companyUser")
                        : t("sidebar.individualUser")}
                    </p>
                  </div>

                  <MoreVertical className="h-4 w-4 ml-auto text-muted-foreground" />
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-1" align="end" side="right">
            <div className="space-y-1">
              {/* Navigation Options */}
              <Button
                variant={selectedView === "profile" ? "default" : "ghost"}
                className="w-full justify-start"
                onClick={() => setSelectedView("profile")}
              >
                <User className="h-4 w-4 mr-2" />
                {t("dashboard.tabs.profile")}
              </Button>
              <Button
                variant={selectedView === "subscription" ? "default" : "ghost"}
                className="w-full justify-start"
                onClick={() => setSelectedView("subscription")}
              >
                <Crown className="h-4 w-4 mr-2" />
                {t("dashboard.tabs.subscription")}
              </Button>

              {/* Contact Support Dialog */}
              <Dialog
                open={isContactSupportOpen}
                onOpenChange={setIsContactSupportOpen}
              >
                <DialogTrigger asChild>
                  <Button variant="ghost" className="w-full justify-start">
                    <HelpCircle className="h-4 w-4 mr-2" />
                    {t("sidebar.support.title")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5" />
                      {t("sidebar.support.title")}
                    </DialogTitle>
                    <DialogDescription>
                      {t("sidebar.support.description")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {t("sidebar.support.emailSupport")}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {import.meta.env.VITE_SUPPORT_EMAIL}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button onClick={handleContactSupport} className="w-full">
                        <Mail className="h-4 w-4 mr-2" />
                        {t("sidebar.support.openEmailApp")}
                      </Button>

                      <p className="text-xs text-muted-foreground text-center">
                        {t("sidebar.support.emailAppNote")}
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <SidebarSeparator className="my-1" />

              {/* Theme & Language Settings */}
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-sm text-muted-foreground">
                  {t("dashboard.appearance")}
                </span>
                <div className="flex items-center gap-1">
                  <ThemeToggle />
                  <LanguageToggle />
                </div>
              </div>

              <SidebarSeparator className="my-1" />
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={async () => {
                  try {
                    await dispatch(logoutUser()).unwrap();
                    navigate("/login");
                  } catch (error) {
                    console.error("Logout error:", error);
                    // Even if logout fails, navigate to login
                    navigate("/login");
                  }
                }}
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t("auth.logout")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
